import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFileDialog } from '@realadvisor/hooks';
import { decodeGlobalId } from '@realadvisor/transform/global-id';
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Info,
  Maximize2,
  Paperclip,
  Send,
  Signature,
  Trash2,
  X,
} from 'lucide-react';
import prettyBytes from 'pretty-bytes';
import { flushSync } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type { EmailTemplateSelectFragment } from '@graphql/__generated__/graphql';
import { fetchApiQuery } from '@graphql/ApolloHasuraWrapper';
import type { EmailEditorSnippetData } from '@modules/_shared/components/email-editor';
import {
  type RichEditorValue,
  appendCustomHTML,
  appendHtml,
  appendSignature as appendSignatureHtml,
  createEmptyValue,
  createValueFromStored,
  escapeText,
  hasSignature,
  removeSignature,
  resolveSuggestions,
  updateSuggestions,
} from '@modules/_shared/components/email-editor/htmlValue';
import { RichEditor } from '@modules/_shared/components/email-editor/QuillRichEditor';
import { addCampaignRecipientSuggestions } from '@modules/_shared/components/email-form/campaignSuggestions';
import {
  type CampaignValidationError,
  isCampaignValidationError,
} from '@modules/_shared/components/email-form/campaignValidation';
import { CampaignValidationDialog } from '@modules/_shared/components/email-form/CampaignValidationDialog';
import { CollapsibleAddressList } from '@modules/_shared/components/email-form/CollapsibleAddressList';
import {
  GET_EMAIL_TEMPLATE_SUGGESTIONS,
  menuSuggestions,
  parseSuggestionsMap,
  recipientSlotIds,
  toSuggestionLanguage,
  withRemovedRecipientsCleared,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { isReconnectFixableSendError } from '@modules/_shared/components/email-form/sendError';
import type {
  EmailFallbackRecipient,
  EmailFormFile,
  EmailFormRecipient,
  EmailFormSentDetails,
  EmailTemplateSuggestion,
  ForwardMessage,
  ReplyToMessage,
} from '@modules/_shared/components/email-form/types';
import { EmailCounterAlert } from '@modules/_shared/components/EmailCounterAlert';
import { EmailTemplatesDialog } from '@modules/_shared/components/EmailTemplatesDialog';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { RaTextField } from '@modules/_shared/components/form/RaTextfield';
import {
  CopyRecipientsTrackingHint,
  hasCopyRecipients,
} from '@modules/_shared/components/listing-marketing/CopyRecipientsTrackingHint';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { ReconnectEmailDialog } from '@modules/_shared/components/ReconnectEmailDialog';
import { RichEditorOutline } from '@modules/_shared/components/RichEditorOutline';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { fetchEmailBody } from '@modules/_shared/utils/fetchEmailBody';
import { useFileUpload } from '@modules/_shared/utils/files/file-upload';
import { email_service_origin, links_origin } from '@shared/config';
import { useLocale } from '@shared/hooks/locale';
import { getUUID, toGlobalId } from '@shared/utils/global-id';

import { AttachToPicker, GET_COMPOSER_CONTACT_MATCHES } from './AttachToPicker';
import {
  type ComposerDraftFile,
  clearComposerDraft,
  isEmptyDraft,
  readComposerDraft,
  writeComposerDraft,
} from './composerDraft';
import {
  type PickerState,
  filingResolved,
  filingSettled,
  sentParentIds,
} from './filingState';
import { isFreeRecipient } from './recipientIds';
import { RecipientsField } from './RecipientsField';
import { ownMailboxAddresses, replyRecipientsFor } from './replyRecipients';
import {
  type TemplateSubject,
  refreshedSubject,
  substituteSubject,
} from './templateSubject';

const paperSx = {
  p: 1.5,
  transition: 'all 0.15s ease',
  overflow: 'hidden',
  '&:hover': {
    borderColor: 'grey.300',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
} as const;

const dialogPaperSx = {
  p: 3,
  overflow: 'hidden',
} as const;

const richEditorCss = {
  // The composer fills its host, as `variant="filled"` provided before the
  // restyle; the shared editor no longer sets a default height.
  height: '100%',
  '.ql-editor': {
    minHeight: '150px',
  },
} as const;

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;
const MAX_ATTACHMENT_SIZE_LABEL = '25MB';

const COMPOSER_SIGNATURE_QUERY = gql(/* GraphQL */ `
  query ComposerSignature {
    get_me {
      me {
        id
        email_signature
      }
    }
  }
`);

const QUOTE_TIMEOUT_MS = 20_000;
// How long Send waits for the template values of a new recipient list.
const SUGGESTIONS_WAIT_MS = 20_000;

// A stable default: a fresh `[]` per render would rebuild the suggestions map,
// and rerun everything keyed on it, on every keystroke.
const NO_SUGGESTIONS: EmailTemplateSuggestion[] = [];

const hasSignatureText = (signature: string | null): boolean => {
  if (!signature) {
    return false;
  }
  return !createValueFromStored(signature).isEmpty();
};

// Standalone template created from a mass-mail compose ("add to templates").
// user_id / tenant_id are preset by the Hasura insert permission.
const SAVE_MASS_MAIL_TEMPLATE = gql(/* GraphQL */ `
  mutation SaveMassMailTemplate($emailTemplate: email_templates_insert_input!) {
    insert_email_templates_one(object: $emailTemplate) {
      id
    }
  }
`);

// The composer holds attachments in the shape the draft stores them, so the
// two cannot drift.
type FileData = ComposerDraftFile;

type Props = {
  onClose: () => void;
  parentGlobalId?: string | null;
  parentGlobalIds?: string[];
  initialRecipients?: EmailFormRecipient[];
  fallbackToRecipients?: EmailFallbackRecipient[];
  templateSuggestions?: EmailTemplateSuggestion[];
  initialTemplate?: EmailTemplateSelectFragment | null;
  initialSubject?: string;
  initialBodyValue?: RichEditorValue | null;
  initialFiles?: EmailFormFile[];
  snippetLotId?: string | null;
  snippetDevelopmentId?: string | null;
  presetSnippet?: EmailEditorSnippetData | null;
  campaignMode?: boolean;
  onEmailSent?: (
    response: unknown,
    sentRecipients: EmailFormRecipient[],
    sent?: EmailFormSentDetails,
  ) => void;
  // Campaign mode only: fired once the mass mail is fully dispatched (and the
  // optional template save succeeded) — the host surfaces the confirmation.
  onCampaignSent?: () => void;
  mode?: 'compose' | 'reply' | 'forward';
  replyToMessage?: ReplyToMessage;
  forwardMessage?: ForwardMessage;
  surface?: 'paper' | 'dialog';
  // The answered message is already filed (see useMessageFiling): the reply
  // inherits it, the "Attach to" picker is not shown.
  inheritsFiling?: boolean;
  // False while the host still reads the answered message's filing: Send
  // waits, since inheritsFiling reads true (picker hidden) in that window.
  filingReady?: boolean;
  // Under the last message of a conversation: an expand action hands the
  // draft over to the dialog.
  onExpand?: () => void;
  // Session draft under this key (see composerDraft.ts): restored at mount,
  // saved as the agent types, cleared on send. No key, no draft.
  draftKey?: string;
  // 'summary' renders the email read-only. Switching variant on the same
  // instance keeps every edit.
  variant?: 'full' | 'summary';
  onModify?: () => void;
  canSaveAsTemplate?: boolean;
  // Full variant only: label of the footer button that calls onClose
  // (defaults to Cancel), and an "Undo changes" button before it.
  closeLabel?: string;
  onRevert?: () => void;
};

type EmailFormValues = {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
};

const computeInitialSubject = ({
  initialSubject,
  mode,
  replyToMessage,
  forwardMessage,
}: {
  initialSubject?: string;
  mode: 'compose' | 'reply' | 'forward';
  replyToMessage?: ReplyToMessage;
  forwardMessage?: ForwardMessage;
}): string => {
  if (initialSubject != null) {
    return initialSubject;
  }
  const subject = replyToMessage?.subject ?? forwardMessage?.subject;
  if (!subject || mode === 'compose') {
    return '';
  }
  const prefix = mode === 'reply' ? 'Re:' : 'Fwd:';
  return subject.startsWith(prefix) ? subject : `${prefix} ${subject}`;
};

type QuoteContext = {
  senderName: string;
  senderEmail: string;
  formattedDate: string;
  messageId: string | null | undefined;
  createdBy: string | null | undefined;
  subject: string;
};

const extractQuoteContext = (
  replyToMessage: ReplyToMessage | undefined,
  forwardMessage: ForwardMessage | undefined,
  language: string,
): QuoteContext => {
  const sender = replyToMessage?.from?.[0] ?? forwardMessage?.from?.[0];
  const emailDate = replyToMessage?.date ?? forwardMessage?.date;
  const formattedDate = emailDate
    ? new Date(emailDate).toLocaleDateString(language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return {
    senderName: sender?.name ?? '',
    senderEmail: sender?.email ?? '',
    formattedDate,
    messageId: replyToMessage?.messageId ?? forwardMessage?.messageId,
    createdBy: replyToMessage?.createdBy ?? forwardMessage?.createdBy,
    subject: forwardMessage?.subject ?? '',
  };
};

type EmailRecipientPayload = {
  email: string;
  name?: string;
  userId?: string;
  buyerLeadId?: string;
};

type CampaignRequestOptions = {
  ignoreMissingRecipientTemplateValues?: boolean;
  validationOnly?: boolean;
};

// Template `cc` / `bcc` are jsonb rows written by EmailTemplateForm's user
// picker: `{ email, name, user_id }`. Only entries carrying a user id can be
// prefilled here — the composer's Cc/Bcc fields are user pickers keyed by id.
const parseTemplateRecipients = (value: unknown): EmailFormRecipient[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry: unknown) => {
    if (entry == null || typeof entry !== 'object') {
      return [];
    }

    const userId = 'user_id' in entry ? entry.user_id : null;
    const email = 'email' in entry ? entry.email : null;
    const name = 'name' in entry ? entry.name : null;

    if (typeof userId !== 'string' || typeof email !== 'string') {
      return [];
    }

    return [
      {
        id: userId,
        email,
        name: typeof name === 'string' && name !== '' ? name : undefined,
      },
    ];
  });
};

const mergeRecipientIds = (
  currentIds: string[],
  added: EmailFormRecipient[],
): string[] => [
  ...currentIds,
  ...added
    .map(recipient => recipient.id)
    .filter(id => !currentIds.includes(id)),
];

const resolveRecipientPayloads = (
  ids: string[],
  selectedRecipientsById: Map<string, EmailFormRecipient>,
  includeUserId: boolean,
): EmailRecipientPayload[] => {
  return ids
    .map(id => selectedRecipientsById.get(id))
    .filter((recipient): recipient is EmailFormRecipient => recipient != null)
    .map(recipient => ({
      email: recipient.email,
      name: recipient.name ?? undefined,
      userId:
        includeUserId && !isFreeRecipient(recipient)
          ? getUUID(recipient.id)
          : undefined,
      buyerLeadId: recipient.buyerLeadId
        ? getUUID(recipient.buyerLeadId)
        : undefined,
    }));
};

const buildSendRequest = ({
  campaignMode,
  toList,
  ccList,
  bccList,
  subject,
  body,
  emailFiles,
  mode,
  replyToMessage,
  forwardMessage,
  activityParentIds,
  activityParentsResolved,
  campaignRequestOptions,
}: {
  campaignMode: boolean;
  toList: EmailRecipientPayload[];
  ccList: EmailRecipientPayload[];
  bccList: EmailRecipientPayload[];
  subject: string;
  body: string;
  emailFiles: Array<{
    name: string;
    url: string;
    size: number;
    mimetype: string;
  }>;
  mode: 'compose' | 'reply' | 'forward';
  replyToMessage?: ReplyToMessage;
  forwardMessage?: ForwardMessage;
  activityParentIds: string[];
  // The composer, not the service, decided the parents (host context, or the
  // agent was shown the matching contacts): an empty list then means "file it
  // nowhere", and the service must not pick a contact by itself.
  activityParentsResolved: boolean;
  campaignRequestOptions: CampaignRequestOptions;
}): { url: string; body: string } => {
  if (campaignMode) {
    return {
      url: `${email_service_origin}/api/nylas/create-and-start-email-campaign`,
      body: JSON.stringify({
        email: {
          to: toList,
          cc: ccList,
          bcc: bccList,
          subjectTemplate: subject,
          bodyTemplate: body,
          files: emailFiles,
        },
        ...campaignRequestOptions,
      }),
    };
  }

  // The message being answered or forwarded: the service inherits the
  // parent activity's filing and visibility from it, as the legacy composer
  // did; a forward starts its new thread with the source thread's visibility.
  const parentMessage =
    mode === 'reply'
      ? replyToMessage
      : mode === 'forward'
      ? forwardMessage
      : null;
  return {
    url: `${email_service_origin}/api/nylas/send-email`,
    body: JSON.stringify({
      email: {
        to: toList.map(({ userId: _, ...recipient }) => recipient),
        cc: ccList.map(({ userId: _, ...recipient }) => recipient),
        bcc: bccList.map(({ userId: _, ...recipient }) => recipient),
        subject,
        body,
        files: emailFiles,
        replyToMessageId:
          mode === 'reply' ? replyToMessage?.messageId : undefined,
      },
      activityParentIds,
      activityParentsResolved,
      parentEmailMessageId:
        parentMessage?.id != null
          ? toGlobalId('EmailMessage', parentMessage.id)
          : undefined,
      // The answered or forwarded thread's visibility: a forward's new
      // thread starts with it, and either mode's activity falls back to it
      // when the answered message has no readable activity to inherit from.
      newThreadVisibility:
        (mode === 'forward'
          ? forwardMessage?.visibility
          : replyToMessage?.visibility) ?? undefined,
    }),
  };
};

const summaryPaperSx = {
  p: 0,
  bgcolor: 'transparent',
} as const;

const summaryPanelSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: '6px',
  bgcolor: 'background.paper',
  overflow: 'hidden',
} as const;

const summaryEditorCss = {
  // The body sits flush inside the summary panel, which paints the surface.
  borderRadius: 0,
  backgroundColor: 'transparent',
  '.dark &': { backgroundColor: 'transparent' },
  // The read-only body has nothing to format: drop the editor's toolbar (its
  // first child) instead of showing it greyed out.
  '& > div:first-of-type': {
    display: 'none',
  },
  '.ql-editor': {
    opacity: 1,
    minHeight: 0,
    maxHeight: '360px',
    overflowY: 'auto',
    padding: '8px 12px',
  },
} as const;

const formatSummaryRecipient = ({ name, email }: EmailFormRecipient) =>
  name ? `${name} <${email}>` : email;

const SummaryField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: 'baseline',
      px: 1.5,
      py: 0.75,
      borderBottom: 1,
      borderColor: 'divider',
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', flexShrink: 0, minWidth: 56 }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Box>
  </Stack>
);

const SummaryRecipients = ({
  label,
  recipients,
}: {
  label: string;
  recipients: EmailFormRecipient[];
}) =>
  recipients.length === 0 ? null : (
    <SummaryField label={label}>
      <Typography variant="body2" component="span">
        <CollapsibleAddressList
          addresses={recipients.map(formatSummaryRecipient)}
        />
      </Typography>
    </SummaryField>
  );

export const EmailForm = ({
  onClose,
  parentGlobalId,
  parentGlobalIds,
  initialRecipients = [],
  fallbackToRecipients = [],
  templateSuggestions = NO_SUGGESTIONS,
  initialTemplate = null,
  initialSubject,
  initialBodyValue = null,
  initialFiles,
  snippetLotId,
  snippetDevelopmentId,
  presetSnippet,
  campaignMode = false,
  onEmailSent,
  onCampaignSent,
  mode = 'compose',
  replyToMessage,
  forwardMessage,
  surface = 'paper',
  inheritsFiling = false,
  filingReady = true,
  onExpand,
  draftKey,
  variant = 'full',
  onModify,
  canSaveAsTemplate = true,
  closeLabel,
  onRevert,
}: Props) => {
  const isSummary = variant === 'summary';
  const { t, language } = useLocale();
  const { me, refetchMe, hasuraRole } = useAppData();
  const { data: signatureData } = useQuery(COMPOSER_SIGNATURE_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const userSignature =
    signatureData?.get_me.me?.email_signature ?? me?.email_signature ?? null;
  const hasValidSignature = hasSignatureText(userSignature);

  // Reply with no recipients given by the caller: the mail client rule (see
  // `replyRecipientsFor`), as bare-address chips; a caller that resolved CRM
  // users passes them instead and this is skipped.
  const ownEmails = useMemo(
    () => (me != null ? ownMailboxAddresses(me) : []),
    [me],
  );
  const replyRecipients = useMemo(
    () =>
      mode === 'reply' && replyToMessage != null
        ? replyRecipientsFor(replyToMessage, ownEmails)
        : null,
    [mode, replyToMessage, ownEmails],
  );
  const startRecipients = {
    to:
      initialRecipients.length > 0
        ? initialRecipients
        : replyRecipients?.to ?? [],
    cc: replyRecipients?.cc ?? [],
    bcc: replyRecipients?.bcc ?? [],
  };
  // A draft left in this tab wins over what the host would prefill: the
  // agent's text, recipients and subject come back as they were.
  const [restoredDraft] = useState(() =>
    draftKey != null ? readComposerDraft(draftKey) : null,
  );
  if (restoredDraft != null) {
    startRecipients.to = restoredDraft.to;
    startRecipients.cc = restoredDraft.cc;
    startRecipients.bcc = restoredDraft.bcc;
  }
  const initialToIds = startRecipients.to.map(recipient => recipient.id);

  const {
    register,
    watch,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<EmailFormValues>({
    defaultValues: {
      to: initialToIds,
      cc: startRecipients.cc.map(recipient => recipient.id),
      bcc: startRecipients.bcc.map(recipient => recipient.id),
      subject:
        restoredDraft?.subject ??
        computeInitialSubject({
          initialSubject,
          mode,
          replyToMessage,
          forwardMessage,
        }),
    },
  });
  const subject = watch('subject');
  const [selectedRecipientsById, setSelectedRecipientsById] = useState(() => {
    const result = new Map<string, EmailFormRecipient>();
    [
      ...startRecipients.to,
      ...startRecipients.cc,
      ...startRecipients.bcc,
    ].forEach(recipient => {
      result.set(recipient.id, recipient);
    });
    return result;
  });

  const toRecipients = watch('to');
  const ccRecipients = watch('cc');
  const bccRecipients = watch('bcc');
  // What the composer opened with (first render = the form defaults): a
  // subject or recipients that differ from it are the agent's.
  const startSubject = useRef(subject);
  const startRecipientIds = useRef(
    [...toRecipients, ...ccRecipients, ...bccRecipients].join(','),
  );

  // A bare address is resolved to its CRM contact so `#recipient.*` fills as
  // for a picked user (not for the `user` role: no select on `emails`).
  const freeToEmails = useMemo(
    () =>
      toRecipients
        .flatMap(id =>
          isFreeRecipient({ id })
            ? [selectedRecipientsById.get(id)?.email.trim().toLowerCase() ?? '']
            : [],
        )
        .filter(email => email !== ''),
    [toRecipients, selectedRecipientsById],
  );
  const { data: contactMatchesData, loading: contactMatchesLoading } = useQuery(
    GET_COMPOSER_CONTACT_MATCHES,
    {
      variables: { emails: freeToEmails },
      skip:
        freeToEmails.length === 0 ||
        hasuraRole === 'user' ||
        templateSuggestions.length > 0,
    },
  );
  const suggestionRecipients = toRecipients.flatMap(id => {
    if (!isFreeRecipient({ id })) {
      return [getUUID(id)];
    }
    const email = selectedRecipientsById.get(id)?.email.trim().toLowerCase();
    const match = contactMatchesData?.users.find(user =>
      user.emails.some(row => row.email.toLowerCase() === email),
    );
    return match != null ? [match.id] : [];
  });
  // Restored with the draft: the picker rebuilds its matches from the same
  // recipients, so both the parents and the contacts already answered for
  // come back rather than being re-guessed in the dialog.
  const [attachParentIds, setAttachParentIds] = useState<string[]>(
    () => restoredDraft?.attachParentIds ?? [],
  );
  // Where the picker's lookup stands (see AttachToPicker). `loading` blocks
  // Send: the agent has had no chance to decide. `unavailable` does not — a
  // failed lookup must not strand a written mail — but the choice it cannot
  // confirm is not sent either, while staying in the draft for a later try.
  const [attachState, setAttachState] = useState<PickerState>('loading');
  // A single lead picked in "Attach to" fills the property keywords; a failed
  // picker lookup or a confirmed inherited filing (no picker) drops it, like
  // the filing that is sent.
  const attachedLeadIds = (
    attachState === 'unavailable' || (inheritsFiling && filingReady)
      ? []
      : attachParentIds
  ).flatMap(id => {
    const decoded = decodeGlobalId(id);
    return decoded?.type === 'Lead' ? [decoded.id] : [];
  });
  const suggestionLeadId =
    attachedLeadIds.length === 1 ? attachedLeadIds[0] : null;
  const suggestionLanguage = toSuggestionLanguage(language);
  const {
    data: composerSuggestionsData,
    loading: composerSuggestionsLoading,
    error: composerSuggestionsError,
  } = useQuery(GET_EMAIL_TEMPLATE_SUGGESTIONS, {
    variables: {
      senderId: me?.id,
      recipients: suggestionRecipients,
      language: suggestionLanguage,
      leadId: suggestionLeadId,
    },
    skip: me?.id == null || templateSuggestions.length > 0,
  });
  const suggestionsLoading =
    composerSuggestionsLoading || contactMatchesLoading;
  // Between two recipient lists the query holds no data: an empty map then
  // means "not known yet", not "no variables".
  const suggestionsReady =
    templateSuggestions.length > 0 ||
    (!suggestionsLoading &&
      composerSuggestionsError == null &&
      composerSuggestionsData != null);
  // A lookup that neither lands nor fails must not strand a written mail.
  // One wait per lookup: `loading` stays true when new variables replace a
  // lookup still in flight, so they re-arm it too.
  const [suggestionsWaitExpired, setSuggestionsWaitExpired] = useState(false);
  const suggestionsLookupKey = `${suggestionRecipients.join(
    ',',
  )}|${suggestionLanguage}|${suggestionLeadId ?? ''}`;
  useEffect(() => {
    setSuggestionsWaitExpired(false);
    if (!suggestionsLoading) {
      return undefined;
    }
    const timer = setTimeout(
      () => setSuggestionsWaitExpired(true),
      SUGGESTIONS_WAIT_MS,
    );
    return () => clearTimeout(timer);
  }, [suggestionsLoading, suggestionsLookupKey]);

  const suggestions = useMemo(() => {
    const contextSuggestions =
      templateSuggestions.length > 0
        ? templateSuggestions
        : // `#recipient.*` kept: a host without its own list (the mailbox)
          // writes to the people in To. Campaign mode swaps them for its
          // per-recipient placeholders just below.
          parseSuggestionsMap(
            composerSuggestionsData?.get_email_template_suggestions
              ?.suggestions,
          );

    return campaignMode
      ? addCampaignRecipientSuggestions(contextSuggestions)
      : contextSuggestions;
  }, [campaignMode, composerSuggestionsData, templateSuggestions]);

  const lookupSuggestionsMap = useMemo(
    () =>
      suggestions.reduce<Record<string, string>>((acc, suggestion) => {
        acc[suggestion.id] = suggestion.value ?? suggestion.name;
        return acc;
      }, {}),
    [suggestions],
  );

  // The applied template's subject follows the recipients until the agent
  // edits the field.
  const templateSubject = useRef<TemplateSubject | null>(
    restoredDraft?.templateSubject ?? null,
  );
  const [bodyValue, setBodyValue] = useState<RichEditorValue>(() =>
    restoredDraft != null
      ? createValueFromStored(restoredDraft.bodyHtml)
      : initialBodyValue ?? createEmptyValue(),
  );
  // Mailbox only, once the lookup answered for the current To: a
  // `#recipient_N.*` it does not return has no recipient behind it and
  // resolves empty. The template's raw subject is read too: the emptied field
  // no longer holds the slot, which would bring the raw variable back.
  const removedRecipientSlots =
    templateSuggestions.length === 0 && !campaignMode && suggestionsReady
      ? recipientSlotIds([
          bodyValue.getHtml(),
          subject,
          templateSubject.current?.raw ?? '',
        ]).join('\n')
      : '';
  const suggestionsMap = useMemo(
    () =>
      withRemovedRecipientsCleared(
        lookupSuggestionsMap,
        removedRecipientSlots === '' ? [] : removedRecipientSlots.split('\n'),
      ),
    [lookupSuggestionsMap, removedRecipientSlots],
  );
  useEffect(() => {
    if (!suggestionsReady) {
      return;
    }
    const next = refreshedSubject({
      template: templateSubject.current,
      current: getValues('subject'),
      suggestionsMap,
    });
    if (next != null && templateSubject.current != null) {
      templateSubject.current = { ...templateSubject.current, applied: next };
      setValue('subject', next);
    }
  }, [suggestionsReady, suggestionsMap, getValues, setValue]);

  // Mass-mail only: optionally save the composed message as a reusable template.
  // Team-less accounts (Hasura role `user`) have no insert permission on
  // email_templates — hide the option instead of offering a doomed save. Gate
  // on the JWT role itself (same as templateMeta.canEditTemplate): an
  // is_admin/teams proxy misses super_admin sessions whose users row has
  // neither.
  const canSaveTemplate =
    canSaveAsTemplate && me != null && hasuraRole !== 'user';
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // The campaign went out but the template insert failed — the dialog stays
  // open (re-send blocked) so the failure is visible and retryable.
  const [templateSaveFailed, setTemplateSaveFailed] = useState(false);
  const [saveMassMailTemplate] = useMutation(SAVE_MASS_MAIL_TEMPLATE);

  const [filingDecided, setFilingDecided] = useState<string[]>(
    () => restoredDraft?.filingDecided ?? [],
  );
  const attachSettled = filingSettled(attachState);
  const rememberRecipients = useCallback((recipients: EmailFormRecipient[]) => {
    setSelectedRecipientsById(prev => {
      const next = new Map(prev);
      recipients.forEach(recipient => {
        next.set(recipient.id, {
          ...recipient,
          buyerLeadId:
            recipient.buyerLeadId ?? prev.get(recipient.id)?.buyerLeadId,
        });
      });
      return next;
    });
  }, []);

  // The form keeps recipient IDS (users' uuids, `email:` keys for bare
  // addresses); the field works on the recipient objects.
  const renderRecipientsField = (
    name: 'to' | 'cc' | 'bcc',
    label: string,
    required: boolean,
    labelAction?: React.ReactNode,
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <RecipientsField
          label={label}
          required={required}
          allowFreeAddresses={!campaignMode}
          labelAction={labelAction}
          autoFocus={name === 'to' && initialToIds.length === 0}
          value={field.value.flatMap(id => {
            const recipient = selectedRecipientsById.get(id);
            return recipient != null ? [recipient] : [];
          })}
          onChange={recipients => {
            rememberRecipients(recipients);
            field.onChange(recipients.map(recipient => recipient.id));
          }}
        />
      )}
    />
  );

  const [bodyEditorRevision, setBodyEditorRevision] = useState(0);
  // Variables already in the body follow the recipients and the attached
  // lead, like the subject. The editor reseeds on a new value; no remount.
  useEffect(() => {
    if (!suggestionsReady) {
      return;
    }
    setBodyValue(current => {
      const next = updateSuggestions(current, suggestionsMap);
      return next.getHtml() === current.getHtml() ? current : next;
    });
  }, [suggestionsReady, suggestionsMap]);
  // Already uploaded, so the draft carries them as data: expanding the
  // composer must not silently drop an attachment the agent added.
  const [files, setFiles] = useState<FileData[]>(
    () => restoredDraft?.files ?? initialFiles ?? [],
  );
  const [pendingFiles, setPendingFiles] = useState<
    Array<{ id: string; name: string; size: number; mimetype: string }>
  >([]);
  const pendingIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campaignValidationError, setCampaignValidationError] =
    useState<CampaignValidationError | null>(null);
  const [campaignValidationSuccess, setCampaignValidationSuccess] = useState<
    string | null
  >(null);
  const [limitReached, setLimitReached] = useState(false);

  // Open when anything is already in Cc / Bcc, whatever put it there (host,
  // reply rule, restored draft): a copy the agent cannot see must not go out.
  const [showCcBcc, setShowCcBcc] = useState(
    !campaignMode &&
      (startRecipients.cc.length > 0 || startRecipients.bcc.length > 0),
  );
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  // A restored draft (or a prefilled body) may already carry the signature.
  const [signatureEnabled, setSignatureEnabled] = useState(() =>
    hasSignature(bodyValue),
  );
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [reconnectDialogOpen, setReconnectDialogOpen] = useState(false);
  const signatureInitialized = useRef(restoredDraft != null);
  // Whether the agent typed in the body (a restored draft counts as typed).
  // The editor reports selection moves as changes too, so a change is a
  // change of HTML against the last value, programmatic updates (quote,
  // signature, template) included via the effect below.
  const userEditedBody = useRef(restoredDraft != null);
  const lastBodyHtml = useRef<string | null>(null);
  useEffect(() => {
    lastBodyHtml.current = bodyValue.getHtml();
  }, [bodyValue]);
  const editorSuggestions = useMemo(
    () =>
      menuSuggestions(
        suggestions,
        templateSuggestions.length === 0 && !campaignMode,
      ),
    [suggestions, templateSuggestions, campaignMode],
  );

  const [uploadFile, uploading] = useFileUpload('file');
  const [uploadImage] = useFileUpload('image');
  const [pendingBodyImages, setPendingBodyImages] = useState(0);
  const handleImageUpload = useCallback(
    (file: File) =>
      new Promise<string | null>((resolve, reject) => {
        uploadImage(file, ({ data, error: uploadError }) => {
          if (uploadError) {
            reject(uploadError);
            return;
          }
          resolve(data?.url ?? null);
        });
      }),
    [uploadImage],
  );
  const openFileDialog = useFileDialog({
    multiple: true,
    onChange: selectedFiles => {
      let selectedSize =
        files.reduce((sum, file) => sum + file.size, 0) +
        pendingFiles.reduce((sum, file) => sum + file.size, 0);
      selectedFiles.forEach(file => {
        if (selectedSize + file.size > MAX_ATTACHMENT_SIZE) {
          setError(
            t('Total attachment size is limited to {{size}}.', {
              size: MAX_ATTACHMENT_SIZE_LABEL,
            }),
          );
          return;
        }
        selectedSize += file.size;

        const pendingId = `pending-${pendingIdRef.current}`;
        pendingIdRef.current += 1;
        setPendingFiles(prev => [
          ...prev,
          {
            id: pendingId,
            name: file.name,
            size: file.size,
            mimetype: file.type,
          },
        ]);

        uploadFile(file, ({ data, error: uploadError }) => {
          setPendingFiles(prev => prev.filter(p => p.id !== pendingId));
          if (data?.url && data?.id) {
            setFiles(prev => [
              ...prev,
              {
                id: data.id,
                name: file.name,
                url: data.url,
                size: file.size,
                mimetype: file.type,
              },
            ]);
          }
          if (uploadError) {
            setError(t('Failed to upload file'));
          }
        });
      });
    },
  });

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const appendSignature = useCallback(
    (currentBodyValue: RichEditorValue) => {
      if (!hasValidSignature || !userSignature) {
        return currentBodyValue;
      }
      if (hasSignature(currentBodyValue)) {
        return currentBodyValue;
      }

      const signatureHtml = updateSuggestions(
        createValueFromStored(userSignature),
        suggestionsMap,
      ).getHtml();
      const newEditorValue = appendSignatureHtml(
        currentBodyValue,
        signatureHtml,
      );

      setSignatureEnabled(true);
      return newEditorValue;
    },
    [hasValidSignature, userSignature, suggestionsMap],
  );

  useEffect(() => {
    if (signatureInitialized.current || !hasValidSignature) {
      return;
    }
    signatureInitialized.current = true;
    setBodyValue(currentBodyValue => appendSignature(currentBodyValue));
  }, [appendSignature, hasValidSignature]);

  // A restored draft already carries its quote (and signature): neither is
  // built again on top of it.
  const quoteInitialized = useRef(restoredDraft != null);
  // The draft is not saved before the prefill (quote fetched, signature
  // appended) has landed: a draft captured earlier would restore without
  // them and, being a draft, keep them from being rebuilt.
  const [prefillSettled, setPrefillSettled] = useState(
    restoredDraft != null || mode === 'compose',
  );
  useEffect(() => {
    if (quoteInitialized.current || mode === 'compose') {
      return;
    }
    quoteInitialized.current = true;

    const {
      senderName,
      senderEmail,
      formattedDate,
      messageId,
      createdBy,
      subject: forwardSubject,
    } = extractQuoteContext(replyToMessage, forwardMessage, language);

    const buildQuote = (originalBody: string | null) => {
      if (mode === 'reply' && senderEmail) {
        const quoteIntro = `On ${formattedDate}, ${escapeText(
          senderName,
        )} &lt;${escapeText(senderEmail)}&gt; wrote:`;

        setBodyValue(prevValue => {
          const withIntro = appendHtml(
            prevValue,
            `<div></div><div></div><div>${quoteIntro}</div>`,
          );
          return originalBody
            ? appendCustomHTML(
                withIntro,
                `<blockquote>${originalBody}</blockquote>`,
              )
            : withIntro;
        });
      } else if (mode === 'forward') {
        const forwardIntro = `<div></div><div>---------- Forwarded message ---------</div>
          <div>From: ${escapeText(senderName)} &lt;${escapeText(
          senderEmail,
        )}&gt;</div>
          <div>Date: ${formattedDate}</div>
          <div>Subject: ${escapeText(forwardSubject)}</div>
          <div></div>`;

        setBodyValue(prevValue => {
          const withIntro = appendHtml(prevValue, forwardIntro);
          return originalBody
            ? appendCustomHTML(withIntro, `<div>${originalBody}</div>`)
            : withIntro;
        });
      }
    };

    if (messageId && createdBy) {
      // Bounded like the reading pane's loader: the draft saver waits for
      // the prefill, so a request that never settles would lose the text
      // typed meanwhile.
      let timer: ReturnType<typeof setTimeout> | undefined;
      Promise.race([
        fetchEmailBody(messageId, createdBy),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error('reply quote timed out')),
            QUOTE_TIMEOUT_MS,
          );
        }),
      ])
        .then(buildQuote)
        .catch((error: unknown) => {
          // The reply opens without the quote; the failure must not vanish.
          console.error('reply quote body fetch failed', error);
          buildQuote(null);
        })
        .finally(() => {
          clearTimeout(timer);
          setPrefillSettled(true);
        });
    } else {
      buildQuote(null);
      setPrefillSettled(true);
    }
  }, [mode, replyToMessage, forwardMessage, language]);

  const applyTemplate = useCallback(
    (template: EmailTemplateSelectFragment) => {
      const rawSubject = template.subject_plaintext ?? template.subject ?? '';
      const newSubject = substituteSubject(rawSubject, suggestionsMap);
      templateSubject.current = { raw: rawSubject, applied: newSubject };
      setValue('subject', newSubject);

      // Campaign mode has no Cc/Bcc fields (its `to` field is the Bcc list).
      if (!campaignMode) {
        const templateCc = parseTemplateRecipients(template.cc);
        const templateBcc = parseTemplateRecipients(template.bcc);

        if (templateCc.length > 0 || templateBcc.length > 0) {
          setSelectedRecipientsById(prev => {
            const next = new Map(prev);
            [...templateCc, ...templateBcc].forEach(recipient => {
              next.set(recipient.id, recipient);
            });
            return next;
          });

          if (templateCc.length > 0) {
            setValue('cc', mergeRecipientIds(getValues('cc'), templateCc));
          }
          if (templateBcc.length > 0) {
            setValue('bcc', mergeRecipientIds(getValues('bcc'), templateBcc));
          }

          setShowCcBcc(true);
        }
      }

      const templateValue = updateSuggestions(
        createValueFromStored(template.text ?? ''),
        suggestionsMap,
      );
      let newBodyValue = templateValue;
      if ((signatureEnabled || template.add_signature) && hasValidSignature) {
        newBodyValue = appendSignature(newBodyValue);
      }
      setBodyValue(newBodyValue);
      setBodyEditorRevision(revision => revision + 1);

      if (template.email_templates_files?.length) {
        const templateFiles = template.email_templates_files
          .filter(tf => tf.file != null)
          .map(tf => ({
            id: tf.file!.id,
            name: tf.file!.name ?? '',
            url: tf.file!.url ?? '',
            size: tf.file!.size ?? 0,
            mimetype: tf.file!.mimetype ?? '',
          }));
        setFiles(prev => [...prev, ...templateFiles]);
      }
    },
    [
      appendSignature,
      campaignMode,
      getValues,
      hasValidSignature,
      signatureEnabled,
      setValue,
      suggestionsMap,
    ],
  );

  const handleTemplateSelect = useCallback(
    (template: EmailTemplateSelectFragment) => {
      setTemplatesDialogOpen(false);
      flushSync(() => {
        setBodyValue(createEmptyValue());
        setBodyEditorRevision(revision => revision + 1);
      });
      applyTemplate(template);
    },
    [applyTemplate],
  );

  const initialTemplateApplied = useRef(false);
  useEffect(() => {
    if (
      initialTemplateApplied.current ||
      mode !== 'compose' ||
      initialTemplate == null
    ) {
      return;
    }

    initialTemplateApplied.current = true;
    signatureInitialized.current = true;
    applyTemplate(initialTemplate);
  }, [applyTemplate, initialTemplate, mode]);

  const handleSignatureToggle = (checked: boolean) => {
    if (checked && !hasValidSignature) {
      setSignatureError(
        t('No signature configured. Please add one in your email settings.'),
      );
      return;
    }

    // The agent's choice, kept in the draft like typed text.
    userEditedBody.current = true;
    if (checked) {
      setBodyValue(appendSignature(bodyValue));
    } else {
      setBodyValue(removeSignature(bodyValue));
      setSignatureEnabled(false);
    }
  };

  const resolveRecipientIds = (ids: string[]) =>
    resolveRecipientPayloads(ids, selectedRecipientsById, campaignMode);

  // Parents given by the host (a lead's feed, a buyer lead) win; a mail from
  // the mailbox has none and gets the ones the agent picked in AttachToPicker.
  const hostParentIds =
    parentGlobalIds ?? (parentGlobalId != null ? [parentGlobalId] : []);
  // Every recipient's address, typed or picked from the search, for the
  // contact matching. Hidden in campaign mode (one mail per recipient, filed
  // by the campaign), when the host set the parents, when the answered
  // message is already filed (the reply inherits it), and for the `user`
  // role (no `emails` select).
  const recipientEmails = useMemo(
    () =>
      [...toRecipients, ...ccRecipients, ...bccRecipients].flatMap(id => {
        const recipient = selectedRecipientsById.get(id);
        return recipient != null ? [recipient.email] : [];
      }),
    [toRecipients, ccRecipients, bccRecipients, selectedRecipientsById],
  );
  const showAttachTo =
    !campaignMode &&
    hostParentIds.length === 0 &&
    !inheritsFiling &&
    hasuraRole !== 'user' &&
    recipientEmails.length > 0;
  // Both rules live in `filingState.ts` with their tests: which parents the
  // request may carry (a choice the picker could not confirm against the
  // CURRENT recipients is held back, not cleared), and whether the composer is
  // claiming a filing decision at all.
  const activityParentIds = sentParentIds({
    hostParentIds,
    showAttachTo,
    state: attachState,
    attachParentIds,
  });

  // The draft follows the agent's typing (debounced); an emptied composer
  // drops it. Runs once on mount too, which is harmless: the initial state
  // reads back identical.
  // A save that is still pending (debounce) when the composer unmounts
  // (close, expand) is flushed then, so the last keystrokes are not lost.
  const pendingDraftSave = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (draftKey == null || !prefillSettled) {
      return undefined;
    }
    const save = () => {
      pendingDraftSave.current = null;
      const draft = currentDraft();
      // A composer merely opened writes nothing (forty triaged replies would
      // fill the session storage with their quoted originals): only what the
      // agent changed against the opening state is a draft.
      // An attachment or an answered "Attach to" is the agent's work too.
      // The picker's own preselection is not: `attachParentIds` alone would
      // make every merely opened composer write a draft.
      const edited =
        userEditedBody.current ||
        subject !== startSubject.current ||
        files.length > 0 ||
        filingDecided.length > 0 ||
        [...toRecipients, ...ccRecipients, ...bccRecipients].join(',') !==
          startRecipientIds.current;
      if (isEmptyDraft(draft, bodyValue.isEmpty()) || !edited) {
        clearComposerDraft(draftKey);
      } else {
        writeComposerDraft(draftKey, draft);
      }
    };
    const currentDraft = () => {
      const recipientsOf = (ids: string[]) =>
        ids.flatMap(id => {
          const recipient = selectedRecipientsById.get(id);
          return recipient != null ? [recipient] : [];
        });
      // The canonical HTML (markers kept): `toString('html')` expands the
      // signature and quote markers, and a restored body without them would
      // get a second signature and a mangled quote.
      return {
        subject,
        bodyHtml: bodyValue.getHtml(),
        to: recipientsOf(toRecipients),
        cc: recipientsOf(ccRecipients),
        bcc: recipientsOf(bccRecipients),
        files,
        attachParentIds,
        filingDecided,
        templateSubject: templateSubject.current,
      };
    };
    pendingDraftSave.current = save;
    const timer = setTimeout(save, 400);
    return () => clearTimeout(timer);
  }, [
    draftKey,
    prefillSettled,
    subject,
    bodyValue,
    toRecipients,
    ccRecipients,
    bccRecipients,
    selectedRecipientsById,
    files,
    attachParentIds,
    filingDecided,
  ]);
  useEffect(
    () => () => {
      pendingDraftSave.current?.();
    },
    [],
  );

  // "Add to templates" insert. Refetches the compose template picker so the
  // new template is usable in the same session.
  const saveComposedTemplate = async (): Promise<boolean> => {
    const savedBody = removeSignature(bodyValue);
    try {
      await saveMassMailTemplate({
        variables: {
          emailTemplate: {
            name: templateName.trim(),
            subject: escapeText(subject.trim()),
            subject_plaintext: subject.trim(),
            text: savedBody.getHtml(),
            text_html: savedBody.toString('html', t, language),
            text_plaintext: savedBody.toString('plaintext'),
            // Strip the inline signature and persist the flag instead, so
            // whoever applies the template gets their OWN signature (same
            // convention as EmailTemplateCardForm). Kept inline, the author's
            // name and phone would go out on every colleague's send, and would
            // go stale the moment the author edits their signature.
            add_signature: hasSignature(bodyValue),
          },
        },
        refetchQueries: ['SearchEmailTemplates'],
      });
      return true;
    } catch (error) {
      // The user only sees a generic "Failed to save the template" — keep the
      // real cause (most likely a Hasura permission rejection) diagnosable.
      console.error('Failed to save the mass-mail template:', error);
      return false;
    }
  };

  // Whatever closes the form while the template retry is pending (header X,
  // Cancel, but also ESC / backdrop click, which bypass any onClick handler —
  // MUI unmounts the dialog children on close), the fact that the campaign
  // itself WAS sent must not be buried: confirm it on unmount. Refs, not a
  // closure — the cleanup would capture stale state.
  const templateSaveFailedRef = useRef(false);
  templateSaveFailedRef.current = templateSaveFailed;
  const onCampaignSentRef = useRef(onCampaignSent);
  onCampaignSentRef.current = onCampaignSent;
  useEffect(
    () => () => {
      if (templateSaveFailedRef.current) {
        onCampaignSentRef.current?.();
      }
    },
    [],
  );

  const handleRetryTemplateSave = async () => {
    setLoading(true);
    setError(null);
    const saved = await saveComposedTemplate();
    setLoading(false);
    if (saved) {
      // Clear the flag (ref included — the unmount can beat the re-render)
      // so the unmount confirmation does not fire on top of this explicit one.
      templateSaveFailedRef.current = false;
      setTemplateSaveFailed(false);
      onClose();
      onCampaignSent?.();
    } else {
      setError(t('Failed to save the template'));
    }
  };

  const hasBody = !bodyValue.isEmpty();
  // What enables the Send button, and the only thing that stops a send: a
  // disabled button and a silent return must never disagree. No activity
  // parent is needed (a mail from the mailbox has none).
  // Not before the contact matches arrived when the picker is shown, nor
  // before the answered message's filing is known (the picker is hidden
  // while it loads): a send in either window would leave the filing to the
  // service, whose default is tenant-wide where the picker only offers the
  // agent's own contacts.
  // Nor while the suggestions of a new recipient list are in flight: the
  // subject and the body's variables would still carry the previous
  // recipient's values. A lookup that failed, or that outlasts its wait,
  // does not hold the send back.
  // The subject as it leaves: a variable the field still holds (applied while
  // the values were unknown, then edited) resolves at send, as the body's do.
  const sentSubject = substituteSubject(subject.trim(), suggestionsMap);
  const canSend =
    sentSubject.trim() !== '' &&
    toRecipients.length > 0 &&
    (hasBody || files.length > 0) &&
    (!showAttachTo || attachSettled) &&
    filingReady &&
    (!suggestionsLoading || suggestionsWaitExpired);

  const handleSend = async (
    campaignRequestOptions: CampaignRequestOptions = {},
  ) => {
    if (!canSend) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const resolvedBodyValue = resolveSuggestions(bodyValue, suggestionsMap);
      const body = resolvedBodyValue.toString('html', t, language);
      const sentDetails: EmailFormSentDetails = {
        subject: subject.trim(),
        bodyPlaintext: removeSignature(resolvedBodyValue).toString('plaintext'),
      };

      const toList: Array<{
        email: string;
        name?: string;
        userId?: string;
        buyerLeadId?: string;
      }> = resolveRecipientIds(toRecipients);
      const sentRecipients = toRecipients
        .map(id => selectedRecipientsById.get(id))
        .filter(
          (recipient): recipient is EmailFormRecipient => recipient != null,
        );

      if (toList.length === 0) {
        toList.push(...fallbackToRecipients);
      }

      const ccList = resolveRecipientIds(ccRecipients);
      const bccList = resolveRecipientIds(bccRecipients);

      const emailFiles = files.map(({ id: _, ...file }) => file);

      const { url, body: requestBody } = buildSendRequest({
        campaignMode,
        toList,
        ccList,
        bccList,
        subject: sentSubject.trim(),
        body,
        emailFiles,
        mode,
        replyToMessage,
        forwardMessage,
        activityParentIds,
        activityParentsResolved: filingResolved({
          hostParentIds,
          showAttachTo,
          state: attachState,
        }),
        campaignRequestOptions,
      });

      const response = await fetchApiQuery(url, {
        method: 'POST',
        body: requestBody,
      });

      if (response.ok) {
        if (campaignMode && campaignRequestOptions.validationOnly) {
          setCampaignValidationError(null);
          setCampaignValidationSuccess(
            t('Recipient data is complete. Press Send to start the campaign.'),
          );
          return;
        }
        let data: unknown = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        if (draftKey != null) {
          // Sent: no draft to keep, and nothing pending to flush at unmount.
          pendingDraftSave.current = null;
          clearComposerDraft(draftKey);
        }
        // The campaign is dispatched: if this send came from the validation
        // dialog's "Send anyway", unmount it NOW. The template-failure branch
        // below returns without onClose(), and a still-mounted dialog would
        // cover the Retry button while its own "Send anyway" re-enters
        // handleSend directly, bypassing the footer's templateSaveFailed
        // guard: one click, one duplicate mass mail.
        setCampaignValidationError(null);
        if (campaignMode && saveAsTemplate && templateName.trim() !== '') {
          const saved = await saveComposedTemplate();
          if (!saved) {
            // The campaign IS sent: refresh feeds now, but keep the dialog
            // open with the error visible and a retry — closing would hide
            // the failure, re-sending would duplicate the campaign.
            setTemplateSaveFailed(true);
            setError(t('Failed to save the template'));
            onEmailSent?.(data, sentRecipients, sentDetails);
            emitFeedRefresh();
            return;
          }
        }
        onEmailSent?.(data, sentRecipients, sentDetails);
        emitFeedRefresh();
        onClose();
        if (campaignMode) {
          onCampaignSent?.();
        }
      } else {
        const data = await response.json();
        if (campaignMode && isCampaignValidationError(data)) {
          setCampaignValidationError(data);
          return;
        }
        if (isReconnectFixableSendError(response.status, data)) {
          setReconnectDialogOpen(true);
        } else if ([400, 413, 500].includes(response.status)) {
          // A 400 carries zod's issue list under `error`: not a string, and
          // not something a snackbar can render.
          setError(
            typeof data?.message === 'string'
              ? data.message
              : typeof data?.error === 'string'
              ? data.error
              : t('Failed to send email'),
          );
        } else {
          // Other server messages are untranslated English; show a translated
          // error with the status for support, like useStartSyncRequest.
          setError(
            `${t('Something went wrong. Please try again.')} (${
              response.status
            })`,
          );
        }
      }
    } catch (err) {
      setError(t('Failed to send email'));
    } finally {
      setLoading(false);
    }
  };

  const showCopyRecipientsHint = useMemo(
    () =>
      !campaignMode &&
      toRecipients.length === 1 &&
      hasCopyRecipients(ccRecipients, bccRecipients) &&
      resolveSuggestions(bodyValue, suggestionsMap)
        .toString('html', t, language)
        .includes(`${links_origin}/d?`),
    [
      campaignMode,
      toRecipients,
      ccRecipients,
      bccRecipients,
      bodyValue,
      suggestionsMap,
      t,
      language,
    ],
  );

  const selectedRecipients = (ids: string[]): EmailFormRecipient[] =>
    ids.flatMap(id => selectedRecipientsById.get(id) ?? []);

  const hasFileChips = files.length > 0 || pendingFiles.length > 0;
  const fileChipList = (
    <>
      {files.map(file => (
        <Chip
          key={file.id}
          label={`${file.name} (${prettyBytes(file.size)})`}
          icon={<FileIcon mimetype={file.mimetype} />}
          onDelete={isSummary ? undefined : () => handleRemoveFile(file.id)}
          size="small"
          sx={{ mb: `8px !important` }}
        />
      ))}
      {pendingFiles.map(file => (
        <Chip
          key={file.id}
          label={`${file.name} (${prettyBytes(file.size)})`}
          icon={<CircularProgress size={14} thickness={5} sx={{ ml: '5px' }} />}
          size="small"
          sx={{
            mb: `8px !important`,
            color: 'text.secondary',
            bgcolor: 'action.hover',
          }}
        />
      ))}
    </>
  );

  const footerActions = (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        justifyContent: 'flex-end',
      }}
    >
      {draftKey != null && (
        // The pending debounced save must not write it back on unmount.
        <IconButton
          size="small"
          title={t('Delete draft')}
          aria-label={t('Delete draft')}
          onClick={() => {
            pendingDraftSave.current = null;
            clearComposerDraft(draftKey);
            onClose();
          }}
        >
          <Trash2 size={18} />
        </IconButton>
      )}
      {!isSummary && onRevert != null && (
        <Button onClick={onRevert} size="small" disabled={loading || uploading}>
          {t('Undo changes')}
        </Button>
      )}
      <Button onClick={onClose} size="small" disabled={isSummary && loading}>
        {isSummary ? t('Discard') : closeLabel ?? t('Cancel')}
      </Button>
      {isSummary && onModify != null && (
        <Button
          variant="outlined"
          size="small"
          onClick={onModify}
          disabled={loading}
        >
          {t('Modify email')}
        </Button>
      )}
      {templateSaveFailed && (
        <Button
          variant="outlined"
          size="small"
          loading={loading}
          onClick={handleRetryTemplateSave}
        >
          {t('Retry')}
        </Button>
      )}
      <Button
        loading={loading && !templateSaveFailed}
        loadingPosition="end"
        endIcon={<Send />}
        variant="contained"
        onClick={() => handleSend()}
        disabled={
          !canSend ||
          uploading ||
          // Sending now would drop the image still uploading into the body.
          pendingBodyImages > 0 ||
          limitReached ||
          (saveAsTemplate && templateName.trim() === '') ||
          // The campaign already went out — block a duplicate send while
          // the template retry is pending.
          templateSaveFailed
        }
        size="small"
      >
        {t('Send')}
      </Button>
    </Stack>
  );

  return (
    <Paper
      variant={!isSummary && surface === 'paper' ? 'outlined' : undefined}
      elevation={0}
      sx={
        isSummary
          ? summaryPaperSx
          : surface === 'paper'
          ? paperSx
          : dialogPaperSx
      }
    >
      {isSummary ? (
        <>
          <CopyRecipientsTrackingHint show={showCopyRecipientsHint} />
          <Box sx={summaryPanelSx}>
            <SummaryRecipients
              label={campaignMode ? t('Bcc') : t('To')}
              recipients={selectedRecipients(toRecipients)}
            />
            {!campaignMode && (
              <>
                <SummaryRecipients
                  label={t('Cc')}
                  recipients={selectedRecipients(ccRecipients)}
                />
                <SummaryRecipients
                  label={t('Bcc')}
                  recipients={selectedRecipients(bccRecipients)}
                />
              </>
            )}
            <SummaryField label={t('Subject')}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {subject}
              </Typography>
            </SummaryField>
            <RichEditor
              key={bodyEditorRevision}
              value={bodyValue}
              onChange={() => {}}
              css={summaryEditorCss}
              snippetPreviewLanguage={language}
              snippetPreviewT={t}
              disabled
              pinSignatureToBottom
            />
            {hasFileChips && (
              <Stack
                spacing={1}
                direction="row"
                useFlexGap
                sx={{ flexWrap: 'wrap', px: 1.5, pt: 1 }}
              >
                {fileChipList}
              </Stack>
            )}
          </Box>
          <Stack
            direction="row"
            useFlexGap
            sx={{
              mt: 1,
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <EmailCounterAlert variant="compact" onCount={setLimitReached} />
            <Box sx={{ ml: 'auto' }}>{footerActions}</Box>
          </Stack>
        </>
      ) : (
        <Stack spacing={2}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              {
                {
                  compose: t('Send an email'),
                  reply: t('Send a reply'),
                  forward: t('Forward email'),
                }[mode]
              }
            </Typography>
            <Stack direction="row" sx={{ gap: 0.5 }}>
              {onExpand != null && (
                <IconButton
                  size="small"
                  // An upload still in flight is not in the draft (only the
                  // stored file is), and this composer unmounts on expand: the
                  // attachment would vanish with it. Same guard as Send.
                  disabled={uploading || pendingFiles.length > 0}
                  onClick={() => {
                    // The dialog reads the draft as it mounts: what is still
                    // pending must be on disk before, not at this unmount.
                    pendingDraftSave.current?.();
                    onExpand();
                  }}
                  aria-label={t('Expand')}
                  title={t('Expand')}
                >
                  <Maximize2 size={18} />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={onClose}
                aria-label={t('Close')}
              >
                <X size={18} />
              </IconButton>
            </Stack>
          </Stack>

          {renderRecipientsField(
            'to',
            campaignMode ? t('Bcc') : t('To'),
            true,
            campaignMode ? undefined : (
              <IconButton
                size="small"
                onClick={() => setShowCcBcc(!showCcBcc)}
                sx={{ p: 0.25 }}
                title={showCcBcc ? t('Hide Cc/Bcc') : t('Show Cc/Bcc')}
              >
                {showCcBcc ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </IconButton>
            ),
          )}

          {showCcBcc && (
            <>
              {renderRecipientsField('cc', t('Cc'), false)}
              {renderRecipientsField('bcc', t('Bcc'), false)}
            </>
          )}

          <CopyRecipientsTrackingHint show={showCopyRecipientsHint} />
          {showAttachTo && (
            <AttachToPicker
              emails={recipientEmails}
              value={attachParentIds}
              onChange={setAttachParentIds}
              decided={filingDecided}
              onContactDecided={userId =>
                setFilingDecided(current =>
                  current.includes(userId) ? current : [...current, userId],
                )
              }
              onStateChange={setAttachState}
            />
          )}

          <RaTextField
            name="subject"
            label={t('Subject')}
            register={register}
            errors={errors}
            required
          />

          {/* Same as the legacy composer: the frame is interposed between the
              host and the editor, so it has to carry the percentage through or
              `richEditorCss`'s `height: 100%` resolves against an auto-height
              parent and silently becomes `auto`. */}
          <RichEditorOutline sx={{ height: '100%' }}>
            <RichEditor
              key={bodyEditorRevision}
              value={bodyValue}
              onChange={newBodyValue => {
                if (newBodyValue.getHtml() !== lastBodyHtml.current) {
                  userEditedBody.current = true;
                }
                setBodyValue(newBodyValue);
                setSignatureEnabled(hasSignature(newBodyValue));
              }}
              css={richEditorCss}
              suggestions={editorSuggestions}
              snippetLotId={snippetLotId}
              snippetDevelopmentId={snippetDevelopmentId}
              presetSnippet={presetSnippet}
              snippetPreviewLanguage={language}
              snippetPreviewT={t}
              onImageUpload={handleImageUpload}
              onPendingImageDelta={delta =>
                setPendingBodyImages(n => n + delta)
              }
              pinSignatureToBottom
            />
          </RichEditorOutline>

          {hasFileChips && (
            <Stack
              spacing={1}
              direction="row"
              useFlexGap
              sx={{
                mt: 2,
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                mb: -1,
              }}
            >
              {fileChipList}
            </Stack>
          )}

          <EmailCounterAlert onCount={setLimitReached} sx={{ marginX: 0 }} />

          {campaignMode && canSaveTemplate && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <FormControlLabel
                label={
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: 'center' }}
                  >
                    <span>{t('addToTemplates')}</span>
                    <Tooltip title={t('addToTemplatesTooltip')}>
                      <Box
                        sx={{ display: 'inline-flex', color: 'text.secondary' }}
                      >
                        <Info size={16} />
                      </Box>
                    </Tooltip>
                  </Stack>
                }
                control={
                  <Checkbox
                    checked={saveAsTemplate}
                    size="small"
                    onChange={(_e, checked) => {
                      setSaveAsTemplate(checked);
                      if (checked && templateName.trim() === '') {
                        setTemplateName(subject.trim());
                      }
                    }}
                  />
                }
              />
              {saveAsTemplate && (
                <TextField
                  size="small"
                  label={t('Template name')}
                  value={templateName}
                  error={templateName.trim() === ''}
                  onChange={event => setTemplateName(event.target.value)}
                  sx={{ flexGrow: 1, maxWidth: 320 }}
                />
              )}
            </Stack>
          )}

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={openFileDialog}
                size="small"
                title={t('Attach file')}
              >
                <Paperclip size={20} />
              </IconButton>
              <IconButton
                onClick={() => setTemplatesDialogOpen(true)}
                size="small"
                title={t('Select template')}
              >
                <FileText size={20} />
              </IconButton>
              <IconButton
                onClick={() => handleSignatureToggle(!signatureEnabled)}
                size="small"
                title={t('Add signature')}
                color={signatureEnabled ? 'primary' : 'default'}
              >
                <Signature size={20} />
              </IconButton>
            </Stack>

            {footerActions}
          </Stack>
        </Stack>
      )}
      {error && (
        <RASnackbar
          onClose={() => setError(null)}
          message={error}
          severity="error"
        />
      )}
      {signatureError && (
        <RASnackbar
          onClose={() => setSignatureError(null)}
          message={signatureError}
          severity="error"
        />
      )}
      {campaignValidationSuccess && (
        <RASnackbar
          onClose={() => setCampaignValidationSuccess(null)}
          message={campaignValidationSuccess}
          severity="success"
        />
      )}
      <EmailTemplatesDialog
        open={templatesDialogOpen}
        onClose={() => setTemplatesDialogOpen(false)}
        onTemplateSelect={handleTemplateSelect}
      />
      {campaignValidationError && (
        <CampaignValidationDialog
          error={campaignValidationError}
          loading={loading}
          onEditEmail={() => {
            setCampaignValidationError(null);
            if (isSummary) {
              onModify?.();
            }
          }}
          onRefresh={() => handleSend({ validationOnly: true })}
          onSendAnyway={() =>
            handleSend({ ignoreMissingRecipientTemplateValues: true })
          }
          t={t}
        />
      )}
      <ReconnectEmailDialog
        open={reconnectDialogOpen}
        onClose={() => setReconnectDialogOpen(false)}
        onSuccess={() => {
          setReconnectDialogOpen(false);
          // The login row is no longer `invalid`; drop the app-wide banner and
          // re-enable the compose entry points that read it. Not awaited: the
          // send is what the user asked for, and a failed refresh only means
          // the banner lingers (it is logged by the provider).
          refetchMe().catch(() => {});
          handleSend();
        }}
      />
    </Paper>
  );
};
